import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SEO from "@/components/SEO";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO title="Set new password | NewStarAds" description="Set a new password for your NewStarAds account." />
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl"><span className="grid place-items-center w-8 h-8 rounded-lg bg-primary"><Youtube className="w-5 h-5" /></span> NewStarAds</Link>
        <div><h2 className="font-display text-4xl font-bold tracking-tight leading-tight">Set a new password</h2><p className="mt-4 text-slate-400">Choose a strong password you don't use elsewhere.</p></div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} NewStarAds</p>
      </div>
      <div className="flex items-center justify-center p-6">
        {!token ? (
          <div className="w-full max-w-sm text-center">
            <h1 className="font-display text-2xl font-bold">Invalid link</h1>
            <p className="mt-2 text-slate-500 text-sm">This reset link is missing or invalid. Please request a new one.</p>
            <Link to="/forgot-password"><Button className="mt-6 rounded-full">Request reset link</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="reset-form">
            <div><h1 className="font-display text-3xl font-bold tracking-tight">New password</h1></div>
            <div className="space-y-1.5"><Label>New password</Label><Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} /></div>
            <div className="space-y-1.5"><Label>Confirm password</Label><Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full" data-testid="reset-submit">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
